/**
 * Chat module request config — the CHAT-audience scope + baseUrl for the two
 * endpoints that BYPASS the @andavo/web-ui/api configureApi wrapper
 * (chat-stream.ts SSE + upload.ts multipart). Both must carry a token minted for
 * the EXPERTS backend audience, so they pass the chat scope explicitly to the
 * scope-parameterized acquireToken(scopes) (the auth_unification seam) — NOT the
 * full union, NOT a hardcoded literal.
 *
 * Resolved from the shell's auth config (build-time VITE_*), so the scope is
 * config-driven and tracks whatever the Entra topology turns out to be:
 *   - VITE_CHAT_SCOPES   — explicit chat-audience scope(s); preferred.
 *   - falls back to the full configured scope union if unset (single-registration
 *     dev default — every scope mints from the one SPA app, so the union is the
 *     chat token too).
 *
 * baseUrl is same-origin ('') today (the edge serves the SPA alongside the
 * experts backend). VITE_CHAT_API_BASE_URL overrides it if chat ever needs a
 * distinct origin before the edge fan-out lands.
 */
import { loadAuthConfig } from '@/auth/config';

function parseScopes(raw: string | undefined): string[] {
  if (!raw) return [];
  return raw
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** The CHAT-audience scopes acquireToken() should mint a token for. */
export function getChatScopes(): string[] {
  const explicit = parseScopes(import.meta.env.VITE_CHAT_SCOPES);
  if (explicit.length > 0) return explicit;
  // Single-registration fallback: the full configured union (every scope mints
  // from the one SPA app, so this IS the chat-audience token in that topology).
  return loadAuthConfig().scopes;
}

/** Base URL for the chat endpoints (same-origin by default). */
export function getChatBaseUrl(): string {
  return import.meta.env.VITE_CHAT_API_BASE_URL ?? '';
}

/** Join a same-origin or absolute base with a relative API path. */
export function chatUrl(path: string): string {
  const base = getChatBaseUrl();
  if (!base) return path;
  return `${base.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
}
